import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Currency master (Masters → Currencies). ISO 4217 code + symbol + decimal
 * precision. See ADR-0016 for the standard master shape.
 */

/** ISO 4217 alpha code, normalized to uppercase (e.g. "INR"). */
export const CurrencyAlphaCodeSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z]{3}$/, 'Must be a 3-letter ISO 4217 code')
  .transform((s) => s.toUpperCase());

export const CurrencyListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  symbol: z.string().nullable(),
  decimals: z.number().int(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type CurrencyListItem = z.infer<typeof CurrencyListItemSchema>;

export const CurrencyListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type CurrencyListQuery = z.infer<typeof CurrencyListQuerySchema>;

export const CurrencyListResponseSchema = paginatedResponseSchema(CurrencyListItemSchema);
export type CurrencyListResponse = z.infer<typeof CurrencyListResponseSchema>;

export const CurrencyDetailSchema = CurrencyListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type CurrencyDetail = z.infer<typeof CurrencyDetailSchema>;

const CurrencyCoreFieldsSchema = z.object({
  code: CurrencyAlphaCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  symbol: z.string().trim().max(8).nullable().optional(),
  decimals: z.number().int().min(0).max(8).optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateCurrencyRequestSchema = CurrencyCoreFieldsSchema;
export type CreateCurrencyRequest = z.infer<typeof CreateCurrencyRequestSchema>;

export const UpdateCurrencyRequestSchema = CurrencyCoreFieldsSchema.partial();
export type UpdateCurrencyRequest = z.infer<typeof UpdateCurrencyRequestSchema>;

// ---------------------------------------------------------------------------
// Options (slim dropdown shape, mirrors geo.ts CountryOption)
// ---------------------------------------------------------------------------

export const CurrencyOptionSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  symbol: z.string().nullable(),
});
export type CurrencyOption = z.infer<typeof CurrencyOptionSchema>;

export const CurrencyOptionListSchema = z.array(CurrencyOptionSchema);
export type CurrencyOptionList = z.infer<typeof CurrencyOptionListSchema>;

export const CurrencyOptionsQuerySchema = z.object({
  /** Case-insensitive contains match on code / name. */
  q: z.string().trim().min(1).max(120).optional(),
  /** Defaults to active-only; pass false to include deactivated rows. */
  activeOnly: BooleanQueryParamSchema.optional(),
});
export type CurrencyOptionsQuery = z.infer<typeof CurrencyOptionsQuerySchema>;
